import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";

const SITE_NAME = "Way2Efiling";
const DEFAULT_BASE = "https://way2efiling.com";

// Service slug → human info for Service schema. Keep short and factual.
const SERVICE_INFO = {
  "itr-filing": {
    name: "ITR Filing",
    description:
      "Expert-assisted Income Tax Return filing for salaried, business, and capital-gains taxpayers in India.",
    category: "Tax Preparation",
  },
  "gst-registration": {
    name: "GST Registration",
    description:
      "Online GST registration (GSTIN) for proprietors, partnerships, LLPs, and companies across India.",
    category: "Tax Registration",
  },
  udyam: {
    name: "Udyam / MSME Registration",
    description:
      "Same-day Udyam (MSME) registration certificate to unlock government subsidies and easy loans.",
    category: "Business Registration",
  },
  "fssai-license": {
    name: "FSSAI License",
    description:
      "Basic, State, and Central FSSAI food-business licensing for restaurants, cloud kitchens, and traders.",
    category: "Business Licensing",
  },
};

const HOME_FAQ = [
  {
    q: "How long does ITR filing take with Way2Efiling?",
    a: "Most Income Tax Returns are filed within 24 hours after your documents are received and verified by our CA team.",
  },
  {
    q: "Do I need a GST number for my small business?",
    a: "GST registration is mandatory if your annual turnover exceeds ₹40 lakh (₹20 lakh for services) or if you sell across state lines.",
  },
  {
    q: "Is Udyam registration free?",
    a: "The government portal is free, but the process requires accurate PAN/Aadhaar linkage. Way2Efiling handles the paperwork for a small service fee.",
  },
  {
    q: "Which FSSAI license do I need — Basic, State, or Central?",
    a: "It depends on your annual turnover. Below ₹12 lakh: Basic. ₹12 lakh–₹20 crore: State. Above ₹20 crore or inter-state: Central.",
  },
  {
    q: "Are my documents safe?",
    a: "Yes. All uploads are encrypted, stored on secure object storage, and accessible only to you and the assigned CA.",
  },
];

const humanize = (slug) =>
  slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Remove all previously-injected JSON-LD scripts we own.
const clearInjectedSchemas = () => {
  document
    .head
    .querySelectorAll('script[type="application/ld+json"][data-seo="w2e"]')
    .forEach((n) => n.remove());
};

const injectSchema = (schema) => {
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-seo", "w2e");
  s.text = JSON.stringify(schema);
  document.head.appendChild(s);
};

const buildOrganization = (base) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  legalName: "SHREE ADARSH INFOTECH",
  url: base + "/",
  logo: `${base}/logo512.png`,
  email: "support@way2efiling.com",
  telephone: "+91-8448440219",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Q1 & Q2, Jain Building, Main Market, Sandiya",
    addressLocality: "Sojat",
    addressRegion: "Rajasthan",
    postalCode: "306306",
    addressCountry: "IN",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+91-8448440219",
      contactType: "customer support",
      areaServed: "IN",
      availableLanguage: ["en", "hi"],
    },
  ],
  sameAs: [],
});

const buildWebsite = (base) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: base + "/",
  potentialAction: {
    "@type": "SearchAction",
    target: `${base}/services?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

const buildBreadcrumbs = (base, pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return null;
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: base + "/",
    },
  ];
  let acc = "";
  parts.forEach((seg, i) => {
    acc += "/" + seg;
    items.push({
      "@type": "ListItem",
      position: i + 2,
      name: humanize(decodeURIComponent(seg)),
      item: base + acc,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
};

const buildService = (base, slug) => {
  const info = SERVICE_INFO[slug] || {
    name: humanize(slug),
    description: `${humanize(slug)} filing service by ${SITE_NAME}.`,
    category: "Professional Services",
  };
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: info.name,
    description: info.description,
    category: info.category,
    serviceType: info.name,
    areaServed: { "@type": "Country", name: "India" },
    provider: {
      "@type": "Organization",
      name: SITE_NAME,
      url: base + "/",
    },
    url: `${base}/service/${slug}`,
  };
};

const buildFaqPage = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: {
      "@type": "Answer",
      text: a,
    },
  })),
});

// Sets/updates <title>, meta description, keywords, canonical, OG tags,
// and JSON-LD structured data (Organization, WebSite, BreadcrumbList,
// Service, FAQPage) — refreshed on every route change.
export default function SEOHead({ title, description, image }) {
  const { settings } = useSiteSettings();
  const { pathname } = useLocation();
  const seo = settings?.seo || {};

  useEffect(() => {
    const finalTitle = title
      ? `${title} | ${seo.title || SITE_NAME}`
      : seo.title || `${SITE_NAME} – Tax & License Filing Simplified`;
    const finalDesc =
      description ||
      seo.description ||
      `${SITE_NAME} helps individuals and businesses file ITR, GST, Udyam, and FSSAI with expert support.`;
    const finalKeywords =
      seo.keywords ||
      "ITR filing, GST registration, Udyam, FSSAI license, tax filing India";
    const finalImage = image || seo.og_image || "";
    const CANONICAL_BASE = (settings?.site_url || DEFAULT_BASE).replace(/\/$/, "");
    const canonical = CANONICAL_BASE + pathname;

    document.title = finalTitle;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", finalDesc);
    setMeta("keywords", finalKeywords);
    setMeta("og:title", finalTitle, "property");
    setMeta("og:description", finalDesc, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", canonical, "property");
    if (finalImage) setMeta("og:image", finalImage, "property");
    setMeta("twitter:card", finalImage ? "summary_large_image" : "summary");
    setMeta("twitter:title", finalTitle);
    setMeta("twitter:description", finalDesc);
    if (finalImage) setMeta("twitter:image", finalImage);

    // canonical
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // favicon / apple-touch-icon (dynamic from site settings)
    const favUrl = settings?.favicon_url;
    if (favUrl) {
      const setIcon = (rel) => {
        let el = document.head.querySelector(`link[rel="${rel}"]`);
        if (!el) {
          el = document.createElement("link");
          el.setAttribute("rel", rel);
          document.head.appendChild(el);
        }
        el.setAttribute("href", favUrl);
      };
      setIcon("icon");
      setIcon("shortcut icon");
      setIcon("apple-touch-icon");
    }

    // ---------- JSON-LD structured data ----------
    clearInjectedSchemas();

    // Always inject Organization + WebSite
    injectSchema(buildOrganization(CANONICAL_BASE));
    injectSchema(buildWebsite(CANONICAL_BASE));

    // Breadcrumbs for non-home routes
    const crumbs = buildBreadcrumbs(CANONICAL_BASE, pathname);
    if (crumbs) injectSchema(crumbs);

    // Service schema for service / apply detail pages
    const serviceMatch = pathname.match(/^\/(?:service|apply)\/([^/?#]+)/);
    if (serviceMatch) {
      injectSchema(buildService(CANONICAL_BASE, serviceMatch[1]));
    }

    // FAQ schema on home page (common tax/filing FAQs)
    if (pathname === "/" || pathname === "") {
      injectSchema(buildFaqPage(HOME_FAQ));
    }
  }, [title, description, image, seo, pathname, settings?.site_url]);

  return null;
}
